import React, { useState, useEffect } from 'react';
import { Box, Container, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import WhatsNewModal from './modals/whatsNew/whatsNewModal';
import DeleteWhatsNewModal from './modals/whatsNew/deleteWhatsNewModal';
import { toast } from 'react-toastify';
import { sortItems } from '../common/utils/tableUtil';
import PageHeader from '../common/banner/pageHeader';
import ClassificationBannerHeader from '../common/banner/classifactionBannerHeader';
import SearchBar from '../common/search/SearchBar';

const WhatsNewTable = ({ items, sortConfig, onSortChange, setItems, fetchItems, currentURL }) => {

  const handleSort = (key) => {
    key = key.toLowerCase();
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  }

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUp fontSize="small" />
    ) : (
      <ArrowDropDown fontSize="small" />
    );
  }

  const itemUpdate = (item) => {
      toast.success(`${item.name} Updated`);
      fetchItems();
  }

  const handleDelete = (item) =>  {
      setItems(items.filter(items => items.id !== item.id));
      toast.success(`${item.name} Deleted`, { autoClose: 3000 });
  }

  const formatDate = (date) => {      
      if (date != null) {
        const incDate = new Date(date);
        const year = incDate.getFullYear();
        const month = (1 + incDate.getMonth()).toString().padStart(2, '0');
        const day = incDate.getDate().toString().padStart(2, '0');
        return (month + '/' + day + '/' + year);
      }
      return '';
  }

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">    
          <TableHead>
            <TableRow sx={{ backgroundColor: '#0568AE' }}>
              {['Created','Name', 'Description'].map((field) => (
                <TableCell
                  key={field}
                  onClick={() => handleSort(field)}
                  sx={{ color: '#EEEEEE', fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                    <SortIcon field={field} />
                  </Stack>
                </TableCell>                
              ))}              
                {
                    currentURL.includes('/admin') ?
                    (  <TableCell sx={{ color: '#EEEEEE', fontWeight: 'bold' }}>Actions</TableCell> ) : ( null )
                }
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={item.id} sx={{ height: 56, backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#000000' }}>
                <TableCell sx={{ color: '#FFFFFF' }}>{formatDate(item.created)}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.name}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.description}</TableCell>
                {
                    currentURL.includes('/admin') ?
                    (  
                        <TableCell> 
                          <WhatsNewModal item={item} onTriggerUpdate={itemUpdate} />
                          <DeleteWhatsNewModal item={item} onTriggerDelete={handleDelete} />
                        </TableCell>
                    ) : ( null )
                }
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default function WhatsNew() {
  const [items, setItems] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const title = 'What\'s New';
  const currentURL = window.location.href;

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/whatsnew/`);    
      if (!response.ok) {
          toast.error(`HTTP error! status: ${response.status}`);
      } else {
          const json = await response.json();
          setItems(json.data);        
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreate = (item) =>  {
        toast.success(`${item.name} Created`, { autoClose: 3000 });
        fetchItems();
  };

  const handleSortChange = (newConfig) => {
    setSortConfig(newConfig);
  };

  const filtered = items.filter((item) => {
    const s = searchQuery.toLowerCase();
    return ['created', 'name', 'description'].some((field) => item[field]?.toLowerCase().includes(s));
  });

  const sorted = sortItems(filtered, sortConfig);
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container sx={{ minWidth:1800, mt: 10, mb: 5 }}>
          <Box sx={{pt: 2}}>
            <ClassificationBannerHeader></ClassificationBannerHeader>
            <PageHeader title={title}></PageHeader>
            <Stack direction="row" justifyContent="flex-end" mt={2} mb={1} sx={{ width: '100%', mx: 'auto' }}>                    
                  <SearchBar
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search Features..."
                      label="Search Features..."
                      width={200}
                  />                
            </Stack>
          
          <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: '#1A1A1A'  }}>
              <WhatsNewTable
                  items={paginated}
                  sortConfig={sortConfig}
                  onSortChange={handleSortChange}
                  setItems={setItems}
                  fetchItems={fetchItems}
                  currentURL={currentURL}
              />
              <TablePagination
                  component="div"
                  count={sorted.length}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
              />
          </TableContainer>
          <Box>
              {
                currentURL.includes('/admin') ?
                ( <WhatsNewModal item='' onTriggerCreate={handleCreate} /> ) : ( null )
              }
          </Box>
        </Box>
    </Container>
  );
}